import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../../../components/Loader';
import PortalApiService from '../../../services/PortalApiService';
import PortalAuthService from '../../../services/PortalAuthService';

const currency = process.env.REACT_APP_CURRENCY || 'AED';
const emptyDuration = { no_hours: '', regular_price: '', discount_price: '' };

const PortalServiceAddUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const company = PortalAuthService.getCurrentUser();
    const isFacility = company?.type === 'facility';

    const [form, setForm] = useState({
        name: '',
        short_desc: '',
        status: true,
        category_id: '',
        sub_category_id: '',
        durations: [{ ...emptyDuration }],
    });
    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [isLoader, setIsLoader] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    // Services are tied to the categories assigned to the company (none assigned = all)
    useEffect(() => {
        PortalApiService.request({ method: 'GET', url: '/getCategories' })
            .then(({ data }) => {
                if (!data.status) return;
                const allowed = (company.categories || []).map(String);
                const list = data.data.categories;
                setCategories(allowed.length ? list.filter(c => allowed.includes(String(c._id))) : list);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!form.category_id) {
            setSubCategories([]);
            return;
        }
        PortalApiService.request({ method: 'GET', url: '/getSubCategories', data: { categories: form.category_id } })
            .then(({ data }) => data.status && setSubCategories(data.data.sub_categories))
            .catch(() => {});
    }, [form.category_id]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                setIsLoader(true);
                const { data } = await PortalApiService.request({ method: 'GET', url: `/services/${id}` });
                if (data.status) {
                    const s = data.data;
                    setForm({
                        name: s.name || '',
                        short_desc: s.short_desc || '',
                        status: !!s.status,
                        category_id: s.category_id?._id || '',
                        sub_category_id: s.sub_category_id?._id || '',
                        durations: s.durations?.length
                            ? s.durations.map(d => ({ no_hours: d.no_hours, regular_price: d.regular_price, discount_price: d.discount_price }))
                            : [{ ...emptyDuration }],
                    });
                    setExistingImages(s.images || []);
                } else {
                    toast.error(data.message);
                    navigate('/services');
                }
            } catch (error) {
                navigate('/services');
            } finally {
                setIsLoader(false);
            }
        })();
    }, [id]);

    const updateDuration = (index, key, value) => {
        set('durations', form.durations.map((d, i) => (i === index ? { ...d, [key]: value } : d)));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = new FormData();
        data.append('name', form.name);
        data.append('short_desc', form.short_desc);
        data.append('status', form.status);

        data.append('durations', JSON.stringify(form.durations));
        data.append('category_id', form.category_id);
        data.append('sub_category_id', form.sub_category_id);

        if (id) {
            data.append('existing_images', JSON.stringify(existingImages));
        }
        newImages.forEach(file => data.append('images', file));

        try {
            setIsSaving(true);
            const { data: res } = await PortalApiService.request({
                method: 'POST',
                url: `/services/createOrUpdate${id ? `/${id}` : ''}`,
                data,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (res.status) {
                toast.success(res.message);
                navigate('/services');
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            // PortalApiService already shows the error toast
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div className="app-toolbar py-3 py-lg-6">
                <div className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading text-dark fw-bold fs-3 my-0">{id ? 'Update' : 'Add'} Service</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/services" className="text-muted text-hover-primary">My Services</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">{id ? 'Update' : 'Add'}</li>
                        </ul>
                    </div>
                    <Link to="/services" className="btn btn-sm btn-light-secondary">
                        <i className="bi bi-arrow-left me-1"></i> Back
                    </Link>
                </div>
            </div>

            <div className="app-content flex-column-fluid">
                <div className="app-container container-xxl">
                    {isLoader ? <Loader text="Loading" size="30" /> : (
                        <form onSubmit={handleSubmit}>
                            <div className="card mb-5">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12 mb-5">
                                            <label className="form-label required">Service Name</label>
                                            <input type="text" className="form-control" required value={form.name}
                                                onChange={e => set('name', e.target.value)}
                                                placeholder={isFacility ? 'e.g. AC Maintenance' : 'Service name'} />
                                        </div>

                                        <div className="col-md-6 mb-5">
                                            <label className="form-label required">Category</label>
                                            <select className="form-select" required value={form.category_id}
                                                onChange={e => setForm(prev => ({ ...prev, category_id: e.target.value, sub_category_id: '' }))}>
                                                <option value="">Select category</option>
                                                {categories.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                            </select>
                                            {isFacility && (
                                                <div className="form-text">The category icon is shown next to the service in the app.</div>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-5">
                                            <label className={`form-label ${isFacility ? '' : 'required'}`}>Sub Category</label>
                                            <select className="form-select" required={!isFacility} value={form.sub_category_id}
                                                onChange={e => set('sub_category_id', e.target.value)}
                                                disabled={!form.category_id}>
                                                <option value="">{isFacility ? 'None' : 'Select sub category'}</option>
                                                {subCategories.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                            </select>
                                        </div>

                                        <div className="col-md-12 mb-5">
                                            <label className="form-label">Description</label>
                                            <textarea className="form-control" rows="3" value={form.short_desc}
                                                onChange={e => set('short_desc', e.target.value)}
                                                placeholder={isFacility ? 'Complete AC inspection and maintenance services.' : ''} />
                                        </div>

                                        <div className="col-md-12 mb-5">
                                            <label className="form-label required">Durations &amp; Pricing</label>
                                            {form.durations.map((d, index) => (
                                                <div className="row g-3 mb-3" key={index}>
                                                    <div className="col-md-3">
                                                        <input type="number" min="1" className="form-control" required placeholder="Hours"
                                                            value={d.no_hours} onChange={e => updateDuration(index, 'no_hours', e.target.value)} />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <input type="number" min="0" step="0.01" className="form-control" required placeholder={`Regular price (${currency})`}
                                                            value={d.regular_price} onChange={e => updateDuration(index, 'regular_price', e.target.value)} />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <input type="number" min="0" step="0.01" className="form-control" required placeholder={`Discount price (${currency})`}
                                                            value={d.discount_price} onChange={e => updateDuration(index, 'discount_price', e.target.value)} />
                                                    </div>
                                                    <div className="col-md-1">
                                                        {form.durations.length > 1 && (
                                                            <button type="button" className="btn btn-icon btn-light-danger"
                                                                onClick={() => set('durations', form.durations.filter((_, i) => i !== index))}>
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <button type="button" className="btn btn-sm btn-light-primary"
                                                onClick={() => set('durations', [...form.durations, { ...emptyDuration }])}>
                                                <i className="bi bi-plus"></i> Add duration
                                            </button>
                                        </div>

                                        <div className="col-md-12 mb-5">
                                            <label className="form-label">Images</label>
                                            <div className="d-flex flex-wrap gap-3 mb-3">
                                                {existingImages.map(src => (
                                                    <div key={src} className="position-relative">
                                                        <img src={src} alt="" className="rounded" style={{ width: 100, height: 100, objectFit: 'cover' }} />
                                                        <button type="button" className="btn btn-icon btn-sm btn-danger position-absolute top-0 end-0"
                                                            onClick={() => setExistingImages(existingImages.filter(i => i !== src))}>
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                                {newImages.map((file, index) => (
                                                    <div key={index} className="position-relative">
                                                        <img src={URL.createObjectURL(file)} alt="" className="rounded" style={{ width: 100, height: 100, objectFit: 'cover' }} />
                                                        <button type="button" className="btn btn-icon btn-sm btn-danger position-absolute top-0 end-0"
                                                            onClick={() => setNewImages(newImages.filter((_, i) => i !== index))}>
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <input type="file" className="form-control" accept="image/*" multiple
                                                onChange={e => { setNewImages([...newImages, ...Array.from(e.target.files)]); e.target.value = ''; }} />
                                            <div className="form-text">The first image is shown as the service cover in the app.</div>
                                        </div>

                                        <div className="col-md-12">
                                            <div className="form-check form-switch form-check-custom form-check-solid">
                                                <input className="form-check-input" type="checkbox" id="service-status" checked={form.status}
                                                    onChange={e => set('status', e.target.checked)} />
                                                <label className="form-check-label" htmlFor="service-status">Active (visible to users)</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-end mb-10">
                                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                    {isSaving ? <Loader size="20" text="Saving" color="white" /> : (id ? 'Update Service' : 'Add Service')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortalServiceAddUpdate;
